import type { AiCostDimension } from '@Application/AiCosts/AiCostDimension';
import resolveAudioLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveAudioLabel';
import formatQuantity from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatQuantity';
import type AiCostBreakdownRowView from '@UI/Store/AiCosts/Views/AiCostBreakdownRowView';

export type SubjectLabel = {
	readonly title: string;
	/** The line under the title: the structure of a facility, its facilities for a structure, the facility of a user. */
	readonly detail: string | null;
	/** A deleted or unattributed subject is drawn in italic, muted. */
	readonly isMuted: boolean;
};

const DELETED_LABELS: Readonly<Record<AiCostDimension, string>> = {
	facilities: 'Agence supprimée',
	organizations: 'Structure supprimée',
	users: 'Utilisateur supprimé',
	documents: 'Document supprimé',
	interviews: 'Entretien supprimé',
	steps: 'Étape supprimée',
};

/** Shown when the cost cannot be attributed (`subjectId` is `null`). */
const UNATTRIBUTED_LABELS: Readonly<Record<AiCostDimension, string>> = {
	facilities: 'Agence supprimée',
	organizations: 'Structure inconnue',
	users: 'Sans utilisateur',
	documents: 'Génération sans document',
	interviews: 'Entretien supprimé',
	steps: 'Étape supprimée',
};

const resolveTitle = (dimension: AiCostDimension, row: AiCostBreakdownRowView): string => {
	if (dimension === 'steps') {
		return row.usageLabel ?? DELETED_LABELS.steps;
	}

	if (row.subjectId === null) {
		return UNATTRIBUTED_LABELS[dimension];
	}

	if (row.isSubjectDeleted) {
		return DELETED_LABELS[dimension];
	}

	if (dimension === 'interviews') {
		return resolveAudioLabel(row.interviewKind, row.subjectDate);
	}

	return row.subjectLabel ?? DELETED_LABELS[dimension];
};

const resolveDetail = (dimension: AiCostDimension, row: AiCostBreakdownRowView): string | null => {
	switch (dimension) {
		case 'facilities':
			return row.organization === null ? null : (row.organization.label ?? 'Structure supprimée');
		case 'organizations':
			return row.facilityCount === null ? null : formatQuantity(row.facilityCount, 'agence', 'agences');
		case 'users':
			return row.facility === null ? null : (row.facility.label ?? 'Agence supprimée');
		default:
			return null;
	}
};

/**
 * The text a breakdown row shows for its subject, per axis: the deleted and unattributed wordings,
 * and the line under the title.
 */
const resolveSubjectLabel = (dimension: AiCostDimension, row: AiCostBreakdownRowView): SubjectLabel => {
	const isMuted = dimension !== 'steps' && (row.subjectId === null || row.isSubjectDeleted);

	return { title: resolveTitle(dimension, row), detail: resolveDetail(dimension, row), isMuted };
};

export default resolveSubjectLabel;
